/*
 * Mobile sticky add to cart bar (snippets/sticky-atc.liquid).
 *
 * Mirrors the main product form rather than duplicating it:
 * - bar select -> main <options-selection> + <variant-selection>
 * - main 'variant-change' -> bar select, price, image
 * - main [data-product-atc] button state/text -> bar button
 * - bar button click -> main button click (Empire handles the add to cart)
 * - bar is hidden while the main [data-product-atc] button is on screen
 */
class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this.select = this.querySelector('[data-sticky-atc-select]');
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.buttonText = this.querySelector('[data-sticky-atc-button-text]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');
    this.compareEl = this.querySelector('[data-sticky-atc-compare]');
    this.imageEl = this.querySelector('[data-sticky-atc-image]');
    this.moneyFormat = this.getAttribute('money-format') || '£{{amount}}';

    try {
      this.product = JSON.parse(this.querySelector('[data-sticky-atc-product]').textContent);
    } catch (error) {
      this.product = null;
    }

    this._onSelectChange = this._onSelectChange.bind(this);
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);

    if (this.select) this.select.addEventListener('change', this._onSelectChange);
    this.button.addEventListener('click', this._onButtonClick);

    document.body.classList.add('has-sticky-atc');
    this._observeHeight();

    // Empire's StaticProduct may move the form (three column layout), so wait
    // for the custom elements to upgrade before binding to the main form.
    customElements.whenDefined('variant-selection').then(() => this._bindMainForm());
  }

  disconnectedCallback() {
    this._connected = false;
    document.body.classList.remove('has-sticky-atc', 'sticky-atc-visible');
    document.documentElement.style.removeProperty('--sticky-atc-height');
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._buttonObserver) this._buttonObserver.disconnect();
    if (this._visibilityObserver) this._visibilityObserver.disconnect();
    if (this.variantSelection) {
      this.variantSelection.removeEventListener('variant-change', this._onVariantChange);
    }
  }

  get scope() {
    return this.closest('.shopify-section') || document;
  }

  get mainButton() {
    return this.scope.querySelector('[data-product-wrapper] [data-product-atc]');
  }

  _bindMainForm() {
    this.variantSelection = this.scope.querySelector('[data-product-wrapper] variant-selection');
    if (!this.variantSelection) {
      this.hidden = true;
      return;
    }

    this.variantSelection.addEventListener('variant-change', this._onVariantChange);

    this.variantSelection.getVariant().then(variant => {
      this._render(variant, this.variantSelection.variant);
    });

    const mainButton = this.mainButton;
    if (!mainButton) {
      this._setHidden(false);
    } else {
      this._observeMainButton(mainButton);
      this._syncButton(mainButton);
      this._buttonObserver = new MutationObserver(() => this._syncButton(mainButton));
      this._buttonObserver.observe(mainButton, {
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  // Only show the bar while the main add to cart button is off screen.
  _observeMainButton(mainButton) {
    if (!('IntersectionObserver' in window)) {
      this._setHidden(false);
      return;
    }

    this._visibilityObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => this._setHidden(entry.isIntersecting));
    });
    this._visibilityObserver.observe(mainButton);
  }

  _setHidden(hidden) {
    this.classList.toggle('sticky-atc--hidden', hidden);
    // Lets other fixed elements (e.g. the Smile launcher) clear the bar only while it's showing
    document.body.classList.toggle('sticky-atc-visible', !hidden);
    this.inert = hidden;
    if (hidden) {
      this.setAttribute('aria-hidden', 'true');
    } else {
      this.removeAttribute('aria-hidden');
    }
  }

  _onSelectChange() {
    if (!this.variantSelection || !this.product) return;

    const id = this.select.value;
    const variant = this.product.variants.find(v => String(v.id) === id);
    if (!variant) return;

    // Update the visible option inputs first so the main form doesn't show a
    // stale selection, then set the variant which fires 'variant-change'.
    const optionsSelection = this.variantSelection.querySelector('options-selection');
    if (optionsSelection && typeof optionsSelection.setSelectedOptions === 'function') {
      optionsSelection.setSelectedOptions(variant.options);
    }

    this.variantSelection.variant = id;
  }

  _onVariantChange(event) {
    const { variant, state } = event.detail;
    this._render(variant, state);
  }

  _onButtonClick() {
    const mainButton = this.mainButton;
    if (!mainButton || mainButton.disabled) return;

    // Surface native validation (e.g. gift card recipient fields) on the main
    // form instead of silently failing off screen.
    const form = mainButton.form;
    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      form.reportValidity();
      return;
    }

    mainButton.click();
  }

  _render(variant, state) {
    if (this.select) {
      const value = variant ? String(variant.id) : 'not-selected';
      if (this.select.value !== value && this.select.querySelector(`option[value="${value}"]`)) {
        this.select.value = value;
      }
      this.classList.toggle('sticky-atc--not-selected', !variant && state === 'not-selected');
    }

    if (!variant) return;

    this.priceEl.textContent = this._formatMoney(variant.price);

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    this.compareEl.hidden = !onSale;
    this.classList.toggle('sticky-atc--on-sale', !!onSale);
    if (onSale) this.compareEl.textContent = this._formatMoney(variant.compare_at_price);

    if (this.imageEl && variant.featured_image && variant.featured_image.src) {
      this.imageEl.src = this._resizeImage(variant.featured_image.src, 96);
    }
  }

  _syncButton(mainButton) {
    const disabled = mainButton.disabled || mainButton.classList.contains('disabled');
    this.button.disabled = disabled;
    this.button.classList.toggle('disabled', disabled);
    this.button.classList.toggle('processing', mainButton.classList.contains('processing'));

    // Empire replaces the button's children with plain text on variant change,
    // so read the label from either shape.
    const mainText = mainButton.querySelector('.atc-button--text') || mainButton;
    const label = mainText.textContent.trim();
    if (label) this.buttonText.textContent = label;
  }

  _observeHeight() {
    const setHeight = () => {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
    };

    setHeight();
    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(setHeight);
      this._resizeObserver.observe(this);
    }
  }

  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  _resizeImage(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src;
    }
  }
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}
